import React from 'react';
import { useTranslation } from 'react-i18next';

const accentClasses = {
  blue: {
    focusRing: 'focus:ring-blue-500',
    button: 'bg-blue-600 hover:bg-blue-700',
    progressFill: 'bg-blue-600',
    progressTrack: 'bg-blue-200',
    progressText: 'text-blue-800',
    progressMeta: 'text-blue-600',
  },
  purple: {
    focusRing: 'focus:ring-purple-500',
    button: 'bg-purple-600 hover:bg-purple-700',
    progressFill: 'bg-purple-600',
    progressTrack: 'bg-purple-200',
    progressText: 'text-purple-800',
    progressMeta: 'text-purple-600',
  }
};

const ApprovalRequestModal = ({
  isOpen,
  title = 'Request Client Approval',
  description,
  countNote,
  noteText,
  approverName,
  approverEmail,
  approverLanguage,
  includeDocuments,
  signedDocumentOptions = [],
  selectedSignedDocumentIds = [],
  allowDocumentUpload = false,
  uploadedFiles = [],
  uploadError = '',
  onFilesChange,
  onFieldChange,
  onCancel,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Send Request',
  accent = 'purple',
  progress = null,
  errors = [],
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const theme = accentClasses[accent] || accentClasses.purple;
  const inputClass = `w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent ${theme.focusRing}`;
  const isSubmitDisabled = isSubmitting || !approverName.trim() || !approverEmail.trim();
  const hasSignedDocumentOptions = signedDocumentOptions.length > 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[100]">
      <div className="relative top-20 mx-auto p-6 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {description && (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">{description}</p>
            {countNote && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800">{countNote}</p>
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Name *
            </label>
            <input
              type="text"
              name="approverName"
              value={approverName}
              onChange={onFieldChange}
              className={inputClass}
              placeholder="John Doe"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Email *
            </label>
            <input
              type="email"
              name="approverEmail"
              value={approverEmail}
              onChange={onFieldChange}
              className={inputClass}
              placeholder="client@example.com"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Language *
            </label>
            <select
              name="approverLanguage"
              value={approverLanguage}
              onChange={onFieldChange}
              className={`${inputClass} bg-white`}
              disabled={isSubmitting}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>

          {includeDocuments !== undefined && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="includeDocuments"
                name="includeDocuments"
                checked={includeDocuments}
                onChange={onFieldChange}
                disabled={isSubmitting}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="includeDocuments" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                Include project documents
                <span className="block text-xs text-gray-500 mt-0.5">
                  Send all project documents along with the final signed document
                </span>
              </label>
            </div>
          )}

          {hasSignedDocumentOptions && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('projectDetails.modals.signedPdfAttachments', 'Signed PDF attachments')}
              </p>
              <div className="max-h-44 overflow-y-auto space-y-2">
                {signedDocumentOptions.map((option) => (
                  <label
                    key={option.id}
                    htmlFor={`signedDocument-${option.id}`}
                    className="flex items-start gap-3 rounded-md bg-white border border-gray-200 p-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      id={`signedDocument-${option.id}`}
                      name="selectedSignedDocumentIds"
                      value={option.id}
                      checked={selectedSignedDocumentIds.includes(option.id)}
                      onChange={onFieldChange}
                      disabled={isSubmitting}
                      className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-800 truncate">
                        {option.label}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {option.versionLabel}
                        {option.sentAt ? ` · ${t('projectDetails.finalDocument.sent', 'Sent')}` : ''}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {allowDocumentUpload && onFilesChange && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('projectDetails.modals.uploadAdditionalDocuments', 'Upload additional documents')}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {t('projectDetails.modals.chooseFiles', 'Choose files')}
              </p>
              <input
                type="file"
                multiple
                onChange={(e) => onFilesChange(Array.from(e.target.files || []))}
                disabled={isSubmitting}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200"
              />
              <p className="mt-2 text-xs text-gray-500">
                {t('projectDetails.modals.filesSavedToProject', 'Files will be saved to this project.')}
              </p>

              {uploadedFiles.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    {t('projectDetails.modals.selectedFiles', 'Selected files')} ({uploadedFiles.length})
                  </p>
                  <ul className="max-h-24 overflow-y-auto space-y-1">
                    {uploadedFiles.map((file, idx) => (
                      <li key={`${file.name}-${idx}`} className="text-xs text-gray-600 truncate">
                        {file.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploadError && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                  <p className="text-xs text-red-700">{uploadError}</p>
                </div>
              )}
            </div>
          )}

          {noteText && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">{noteText}</p>
            </div>
          )}

          {isSubmitting && progress?.total > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm font-medium ${theme.progressText}`}>
                  Sharing documents with approver...
                </p>
                <p className={`text-sm ${theme.progressMeta}`}>
                  {progress.current} / {progress.total}
                </p>
              </div>
              <div className={`w-full ${theme.progressTrack} rounded-full h-2`}>
                <div
                  className={`${theme.progressFill} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium mb-1">
                ⚠️ {errors.length} document{errors.length > 1 ? 's' : ''} failed to share
              </p>
              <p className="text-xs text-red-600">
                Approval will be sent only for successfully shared documents.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme.button}`}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalRequestModal;
